import { useState } from 'react';
import { FileJson, Trash2 } from 'lucide-react';
import { I18n } from '@/i18n/I18n';
import { Button } from '../../ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';

/**
 * A report's or a schema's settings: its title and description, for a report
 * which schema it reads — said here, changed in schema.json like any other
 * file — and deleting it.
 */
export function SettingsDialog({ kind, title: initialTitle, description: initialDescription, schemaLine = null, onOpenSchemaRef, onHide, onSave, onDelete }: {
  kind: 'report' | 'schema';
  title: string;
  description: string;
  /** which schema a report reads, and its draft or which version; null for a run report, or a schema */
  schemaLine?: string | null;
  /** open schema.json; absent when the bundle has none */
  onOpenSchemaRef?: () => void;
  onHide: () => void;
  /** false: not saved, and the dialog stays open */
  onSave: (data: { title: string; description: string }) => Promise<boolean | void>;
  onDelete: () => void;
}) {
  const [title, setTitle] = useState(initialTitle);
  const [description, setDescription] = useState(initialDescription);
  return (
    <Dialog open onOpenChange={(open) => { if (!open) onHide(); }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader><DialogTitle>{I18n.get(kind === 'schema' ? 'designerSchemaSettings' : 'designerReportSettings')}</DialogTitle></DialogHeader>
        <div className="grid gap-2">
          <Label htmlFor="bundle-title">{I18n.get('reportBundleTitle')}</Label>
          <Input id="bundle-title" value={title} onChange={(e) => setTitle(e.target.value)} />
        </div>
        <div className="grid gap-2">
          <Label htmlFor="bundle-description">{I18n.get('reportBundleDescription')}</Label>
          <Textarea id="bundle-description" rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
        </div>
        {schemaLine !== null && (
          <div className="grid gap-1.5">
            <span className="text-sm font-medium">{I18n.get('designerSchemaItReads')}</span>
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span>{schemaLine}</span>
              {onOpenSchemaRef && <Button variant="outline" size="xs" onClick={onOpenSchemaRef}><FileJson /> {I18n.get('designerOpenSchemaRef')}</Button>}
            </div>
            <p className="text-xs text-muted-foreground">{I18n.get('designerSchemaItReadsHelp')}</p>
          </div>
        )}
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border-[1px] border-destructive/30 p-[0.75rem]">
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium">{I18n.get(kind === 'schema' ? 'schemaDelete' : 'reportBundleDeleteBundle')}</p>
            <p className="text-xs text-muted-foreground">{I18n.get(kind === 'schema' ? 'designerDeleteSchemaHelp' : 'designerDeleteHelp')}</p>
          </div>
          <Button variant="outline" size="sm" className="text-destructive hover:bg-destructive/10 hover:text-destructive" onClick={onDelete}><Trash2 /> {I18n.get('designerDelete')}</Button>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onHide}>{I18n.get('cancel')}</Button>
          <Button disabled={title.trim() === ''} onClick={() => void onSave({ title: title.trim(), description: description.trim() }).then((saved) => { if (saved !== false) onHide(); })}>{I18n.get('save')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
